import { makeStyles } from '@material-ui/core/styles'

const FIELD_HEIGHT = 48
const FIELD_RADIUS = 12
const FIELD_FILL = '#F5F7FA'
const DATE_TIME_PAIR_MIN_WIDTH = 290

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(3),
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #dfe3ea',
    borderRadius: 16,
    padding: theme.spacing(3),
    backgroundColor: theme.palette.common.white,
    containerType: 'inline-size',
  },
  heading: {
    margin: 0,
    marginBottom: theme.spacing(2.5),
    textAlign: 'center',
    color: theme.palette.secondary.dark,
    fontWeight: 700,
    fontSize: '1.25rem',
    lineHeight: 1.3,
  },
  offeringField: {
    marginBottom: theme.spacing(1.5),
  },
  /**
   * Stacked by default; date and time pair up once the widget itself is
   * wide enough for the widest labels ("Wed, Sep 30" / "10:30 AM") plus
   * icon and arrow chrome. Sized by container, not viewport, because the
   * md sidebar is narrower than a phone-width widget.
   */
  dateTimeRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
    [`@container (min-width: ${DATE_TIME_PAIR_MIN_WIDTH}px)`]: {
      gridTemplateColumns: 'minmax(0, 1.16fr) minmax(0, 1fr)',
      gap: theme.spacing(1),
    },
  },
  field: {
    display: 'flex',
    alignItems: 'center',
    minHeight: FIELD_HEIGHT,
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    padding: `0 ${theme.spacing(1.5)}px`,
    backgroundColor: FIELD_FILL,
    borderRadius: FIELD_RADIUS,
    boxSizing: 'border-box',
  },
  dateTimeField: {
    [`@container (min-width: ${DATE_TIME_PAIR_MIN_WIDTH}px)`]: {
      paddingLeft: theme.spacing(1.25),
      paddingRight: theme.spacing(0.5),
      '& $fieldIcon': {
        marginRight: theme.spacing(0.75),
      },
      '& $selectInput': {
        paddingRight: `${theme.spacing(3)}px !important`,
      },
      '& $selectIcon': {
        right: 0,
      },
    },
  },
  fieldIcon: {
    flexShrink: 0,
    color: theme.palette.secondary.dark,
    marginRight: theme.spacing(1.25),
  },
  select: {
    flex: '1 1 0%',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    '&:before, &:after': {
      display: 'none',
    },
  },
  selectInput: {
    display: 'block',
    minWidth: 0,
    width: '100%',
    maxWidth: '100%',
    minHeight: FIELD_HEIGHT,
    paddingTop: 0,
    paddingBottom: 0,
    paddingRight: `${theme.spacing(5)}px !important`,
    backgroundColor: 'transparent !important',
    color: theme.palette.secondary.dark,
    fontWeight: 600,
    fontSize: '0.9375rem',
    lineHeight: `${FIELD_HEIGHT}px`,
    boxSizing: 'border-box',
  },
  selectValueText: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  offeringLabelWrap: {
    flex: '1 1 0%',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
  },
  offeringLabel: {
    display: 'block',
    margin: 0,
    color: theme.palette.secondary.dark,
    fontWeight: 600,
    fontSize: '0.9375rem',
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  fieldRefreshing: {
    opacity: 0.55,
    pointerEvents: 'none',
    transition: 'opacity 200ms ease',
  },
  timesPanelRefreshing: {
    opacity: 0.45,
    transition: 'opacity 200ms ease',
  },
  selectIcon: {
    color: theme.palette.secondary.dark,
    right: theme.spacing(0.5),
    pointerEvents: 'none',
  },
  timesHeading: {
    margin: 0,
    marginBottom: theme.spacing(1.5),
    color: theme.palette.secondary.dark,
    fontWeight: 700,
    fontSize: '1rem',
  },
  timesList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: theme.spacing(1),
    margin: 0,
    listStyle: 'none',
    padding: 0,
  },
  rootSidebar: {
    [theme.breakpoints.up('md')]: {
      marginTop: 0,
      padding: theme.spacing(2.5),
      boxShadow: '0 8px 24px rgba(15, 34, 52, 0.06)',
    },
  },
  timesListSidebar: {
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      '& button, & a': {
        whiteSpace: 'nowrap',
        paddingLeft: theme.spacing(0.5),
        paddingRight: theme.spacing(0.5),
      },
    },
  },
  timesListSolo: {
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
  timesListItem: {
    minWidth: 0,
    maxWidth: '100%',
  },
  timeButton: {
    width: '100%',
    textAlign: 'center',
    minHeight: 44,
    padding: `${theme.spacing(1.25)}px ${theme.spacing(1.5)}px`,
    borderRadius: 8,
    fontWeight: 700,
    fontSize: '0.875rem',
    textTransform: 'none',
    lineHeight: 1.2,
    boxShadow: 'none',
    [theme.breakpoints.down('xs')]: {
      padding: `${theme.spacing(1)}px ${theme.spacing(0.75)}px`,
      fontSize: '0.8125rem',
    },
    '&&': {
      backgroundColor: FIELD_FILL,
      color: theme.palette.secondary.dark,
      border: '1px solid #dfe3ea',
      '&:hover, &:focus-visible': {
        backgroundColor: theme.palette.blue.main,
        borderColor: theme.palette.blue.main,
        color: theme.palette.common.white,
        boxShadow: 'none',
        textDecoration: 'none',
      },
    },
  },
  allTimesButton: {
    '&&': {
      whiteSpace: 'normal',
      lineHeight: 1.15,
      textAlign: 'center',
    },
  },
  timesStatus: {
    margin: 0,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: '0.9375rem',
  },
  timesLoading: {
    marginBottom: theme.spacing(2),
  },
}))

export default useStyles
